/* global axios */
import { ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';

export function useWhatsAppOutboundCall() {
  const store = useStore();
  const peerConnection = ref(null);
  const localStream = ref(null);

  const initiateCall = async ({ contactId, inboxId, accountId, conversationId }) => {
    try {
      console.log('[WhatsApp Outbound] Initiating call:', { contactId, inboxId, accountId, conversationId });

      if (!contactId) {
        throw new Error('Contact ID is required');
      }

      // Create RTCPeerConnection
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      });

      peerConnection.value = pc;

      // Monitor connection state
      pc.oniceconnectionstatechange = () => {
        console.log('[WhatsApp Outbound] ICE connection state:', pc.iceConnectionState);
        if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
          console.error('[WhatsApp Outbound] Connection failed or disconnected');
        }
      };

      pc.onconnectionstatechange = () => {
        console.log('[WhatsApp Outbound] Connection state:', pc.connectionState);
      };

      // Get local audio stream
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStream.value = stream;

      console.log('[WhatsApp Outbound] Got local audio stream:', {
        id: stream.id,
        active: stream.active,
        tracks: stream.getTracks().map(t => ({ id: t.id, kind: t.kind, enabled: t.enabled })),
      });

      // Add tracks to peer connection
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
        console.log('[WhatsApp Outbound] Added track:', track.kind, track.id);
      });

      // Handle remote audio
      let remoteAudio = null;
      pc.ontrack = event => {
        console.log('[WhatsApp Outbound] Received remote audio track:', {
          trackId: event.track.id,
          trackKind: event.track.kind,
          streamId: event.streams[0]?.id,
        });

        if (!remoteAudio) {
          remoteAudio = new Audio();
          remoteAudio.autoplay = true;
        }

        remoteAudio.srcObject = event.streams[0];
        remoteAudio
          .play()
          .then(() => console.log('[WhatsApp Outbound] Remote audio playing'))
          .catch(err => console.error('[WhatsApp Outbound] Failed to play remote audio:', err));
      };

      // Create SDP offer (audio only - WhatsApp calling doesn't support video)
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
      });
      await pc.setLocalDescription(offer);

      console.log('[WhatsApp Outbound] Waiting for ICE gathering...');

      // Wait for ICE gathering to complete
      await new Promise(resolve => {
        if (pc.iceGatheringState === 'complete') {
          resolve();
        } else {
          const checkState = () => {
            console.log('[WhatsApp Outbound] ICE gathering state:', pc.iceGatheringState);
            if (pc.iceGatheringState === 'complete') {
              pc.removeEventListener('icegatheringstatechange', checkState);
              resolve();
            }
          };
          pc.addEventListener('icegatheringstatechange', checkState);

          // Fallback timeout
          setTimeout(() => {
            console.log('[WhatsApp Outbound] ICE gathering timeout, proceeding anyway');
            pc.removeEventListener('icegatheringstatechange', checkState);
            resolve();
          }, 5000);
        }
      });

      console.log('[WhatsApp Outbound] ICE gathering complete, sending offer to backend...');
      console.log('[WhatsApp Outbound] SDP Offer:', pc.localDescription.sdp);

      // Send to backend
      const requestBody = {
        contact_id: contactId,
        inbox_id: inboxId,
        sdp_offer: pc.localDescription.sdp,
      };

      // Include conversation_id if provided (to use existing conversation)
      if (conversationId) {
        requestBody.conversation_id = conversationId;
      }

      const response = await axios.post(
        `/api/v1/accounts/${accountId}/whatsapp_calls/initiate`,
        requestBody
      );

      const { call_id, conversation_id } = response.data;

      console.log('[WhatsApp Outbound] Call initiated:', { call_id, conversation_id });

      // Store in Vuex
      store.dispatch('whatsappCall/setActiveCall', {
        callId: call_id,
        conversationId: conversation_id,
        status: 'initiating',
        startTime: Date.now(),
        direction: 'outbound',
      });

      store.commit('whatsappCall/SET_JANUS_PLUGIN_HANDLE', pc);

      return { success: true, call_id, conversation_id };
    } catch (error) {
      console.error('[WhatsApp Outbound] Failed to initiate call:', error);
      cleanup();
      throw error;
    }
  };

  const applyAnswer = async sdpAnswer => {
    try {
      if (!peerConnection.value) {
        console.error('[WhatsApp Outbound] No peer connection available');
        return;
      }

      console.log('[WhatsApp Outbound] Applying SDP answer from WhatsApp');

      await peerConnection.value.setRemoteDescription({
        type: 'answer',
        sdp: sdpAnswer,
      });

      console.log('[WhatsApp Outbound] SDP answer applied, media should flow!');
    } catch (error) {
      console.error('[WhatsApp Outbound] Failed to apply SDP answer:', error);
      throw error;
    }
  };

  const cleanup = () => {
    console.log('[WhatsApp Outbound] Cleaning up call resources');

    if (localStream.value) {
      localStream.value.getTracks().forEach(track => track.stop());
      localStream.value = null;
    }

    if (peerConnection.value) {
      peerConnection.value.close();
      peerConnection.value = null;
    }
  };

  return {
    initiateCall,
    applyAnswer,
    cleanup,
  };
}

// Setup message listener for SDP answer
export function useOutboundCallMessageListener() {
  const store = useStore();

  // Track if we've applied the SDP answer for this call
  let sdpAnswerApplied = false;

  useEmitter('message.updated', async message => {
    if (message.content_type !== 'voice_call') return;

    const callData = message.content_attributes?.data;
    const activeCall = store.state.whatsappCall.activeCall;

    if (!callData || !activeCall) return;
    if (callData.call_id !== activeCall.callId) return;
    if (callData.direction !== 'outbound') return;

    // Got SDP answer from webhook! Apply it if we haven't already
    if (callData.sdp_answer && !sdpAnswerApplied) {
      console.log('[WhatsApp Outbound] Received SDP answer from backend, applying...');

      // Get peer connection from Vuex store (stored during initiateCall)
      const pc = store.state.whatsappCall.janusPluginHandle;

      if (!pc) {
        console.error('[WhatsApp Outbound] No peer connection found in Vuex store');
        return;
      }

      try {
        await pc.setRemoteDescription({
          type: 'answer',
          sdp: callData.sdp_answer,
        });

        sdpAnswerApplied = true;
        console.log('[WhatsApp Outbound] SDP answer applied successfully');

        // Update status to ringing if it's still initiating
        if (activeCall.status === 'initiating') {
          store.dispatch('whatsappCall/updateStatus', 'ringing');
        }
      } catch (error) {
        console.error('[WhatsApp Outbound] Failed to apply SDP answer:', error);
      }
    }

    // Handle status updates
    if (callData.status === 'in-progress' || callData.status === 'active') {
      store.dispatch('whatsappCall/updateStatus', 'active');
    } else if (callData.status === 'rejected') {
      store.dispatch('whatsappCall/updateStatus', 'rejected');
    }
  });
}
